import React, { useEffect, useRef, useState } from 'react';
import CustomDateInput from '../../../components/ui/CustomDateInput';
import CustomSelect from '../../../components/ui/CustomSelect';
import { uiStyles } from '../../../theme/uiStyles';

const ASSET_TYPE_OPTIONS = [
  { value: 'Stock',     label: 'Stock' },
  { value: 'ETF',       label: 'ETF' },
  { value: 'Crypto',    label: 'Crypto' },
  { value: 'Bond',      label: 'Bond' },
  { value: 'Commodity', label: 'Commodity' },
  { value: 'Fund',      label: 'Fund' },
];

function AddAssetForm({
  assetType,
  setAssetType,
  ticker,
  setTicker,
  quantity,
  setQuantity,
  buyPrice,
  setBuyPrice,
  buyDate,
  setBuyDate,
  handleAddAsset,
  displayName,
  setDisplayName,
  submitting,
  tickerSuggestions = [],
  nameSuggestions = [],
  tickerSuggestionsLoading = false,
  nameSuggestionsLoading = false,
  onTickerChange,
  onNameChange,
  onSelectSuggestion,
}) {
  const [activeSuggestionField, setActiveSuggestionField] = useState(null);

  const tickerWrapperRef = useRef(null);
  const nameWrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedTicker =
        tickerWrapperRef.current &&
        tickerWrapperRef.current.contains(event.target);

      const clickedName =
        nameWrapperRef.current &&
        nameWrapperRef.current.contains(event.target);

      if (!clickedTicker && !clickedName) {
        setActiveSuggestionField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTickerInputChange = (e) => {
    const value = e.target.value;
    setTicker(value);
    setActiveSuggestionField('ticker');

    if (typeof onTickerChange === 'function') {
      onTickerChange(value);
    }
  };

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setDisplayName(value);
    setActiveSuggestionField('name');

    if (typeof onNameChange === 'function') {
      onNameChange(value);
    }
  };

  const handleSuggestionClick = (item) => {
    if (typeof onSelectSuggestion === 'function') {
      onSelectSuggestion(item);
    } else {
      setTicker(item.symbol || '');
      setDisplayName(item.name || item.symbol || '');
    }

    setActiveSuggestionField(null);
  };

  const renderSuggestions = () => {
    const isTicker = activeSuggestionField === 'ticker';
    const isName = activeSuggestionField === 'name';

    if (!isTicker && !isName) return null;

    const suggestions = isTicker ? tickerSuggestions : nameSuggestions;
    const loading = isTicker ? tickerSuggestionsLoading : nameSuggestionsLoading;

    if (!loading && suggestions.length === 0) return null;

    return (
      <div style={suggestionsBoxStyle}>
        {loading ? (
          <div style={suggestionLoadingStyle}>Loading...</div>
        ) : (
          suggestions.map((item) => (
            <button
              key={(isTicker ? 'ticker-' : 'name-') + item.symbol + '-' + (item.exchange || '')}
              type="button"
              onClick={() => handleSuggestionClick(item)}
              style={suggestionItemStyle}
            >
              <div style={suggestionMainStyle}>
                <strong>{item.symbol}</strong>
                <span style={suggestionNameStyle}>{item.name}</span>
              </div>
              <div style={suggestionMetaStyle}>
                {[item.exchange || item.quoteType || '', item.currency || '']
                  .filter(Boolean)
                  .join(' • ')}
              </div>
            </button>
          ))
        )}
      </div>
    );
  };

  return (
    <section style={cardStyle}>
      <h3 style={titleStyle}>Add New Asset</h3>

      <form onSubmit={handleAddAsset} style={assetFormStyle}>
        <CustomSelect
          value={assetType}
          onChange={setAssetType}
          options={ASSET_TYPE_OPTIONS}
          disabled={submitting}
        />

        <div style={autocompleteWrapperStyle} ref={tickerWrapperRef}>
          <input
            type="text"
            placeholder="Ticker"
            value={ticker}
            onChange={handleTickerInputChange}
            onFocus={() => setActiveSuggestionField('ticker')}
            style={uiStyles.input}
            autoComplete="off"
          />
          {activeSuggestionField === 'ticker' ? renderSuggestions() : null}
        </div>

        <div style={autocompleteWrapperStyle} ref={nameWrapperRef}>
          <input
            type="text"
            placeholder="Name (optional)"
            value={displayName || ''}
            onChange={handleNameInputChange}
            onFocus={() => setActiveSuggestionField('name')}
            style={uiStyles.input}
            autoComplete="off"
          />
          {activeSuggestionField === 'name' ? renderSuggestions() : null}
        </div>

        <input
          type="number"
          step="0.00000001"
          placeholder="Quantity"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          onBlur={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) setQuantity(String(parseFloat(v.toFixed(8))));
          }}
          style={uiStyles.input}
        />

        <input
          type="number"
          step="any"
          placeholder="Buy Price"
          value={buyPrice}
          onChange={(e) => setBuyPrice(e.target.value)}
          style={uiStyles.input}
        />

        <CustomDateInput
          value={buyDate}
          onChange={setBuyDate}
          placeholder="Buy Date"
        />

        <button
          type="submit"
          style={{ ...uiStyles.primaryButton, ...addButtonStyle }}
          disabled={submitting}
        >
          {submitting ? 'Adding...' : 'Add'}
        </button>
      </form>
    </section>
  );
}

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const titleStyle = {
  marginTop: 0,
  marginBottom: '16px',
  color: 'var(--text-primary)',
};

const assetFormStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px',
  alignItems: 'start',
};

const addButtonStyle = {
  gridColumn: '1 / -1',
  width: 'auto',
  minWidth: '160px',
  justifySelf: 'center',
  padding: '12px 48px',
};

const autocompleteWrapperStyle = {
  position: 'relative',
};

const suggestionsBoxStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
  zIndex: 20,
  maxHeight: '280px',
  overflowY: 'auto',
};

const suggestionItemStyle = {
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  padding: '12px',
  cursor: 'pointer',
  color: 'var(--text-primary)',
};

const suggestionMainStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const suggestionNameStyle = {
  color: 'var(--text-secondary)',
  fontSize: '13px',
};

const suggestionMetaStyle = {
  marginTop: '6px',
  fontSize: '12px',
  color: 'var(--text-secondary)',
};

const suggestionLoadingStyle = {
  padding: '12px',
  color: 'var(--text-secondary)',
};

export default AddAssetForm;